/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import format from 'date-fns/format';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Container, ContainerProps } from 'src/core/components/Container';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExpandableCard } from 'src/core/components/ExpandableCard';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist, important } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { LuisDateFormats, getDateLocale } from 'src/core/utils/supportedLocales';
import { IntentsWithErrorsCard } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/IntentsWithErrorsCard';
import { OverallPredictionsCard } from 'src/luis/projects/analytics/components/OverallPredictionsCard/OverallPredictionsCard';
import { PredictionsPerIntentCard } from 'src/luis/projects/analytics/components/PredictionsPerIntentCard/PredictionsPerIntentCard';
import { NonTrainedPlaceholder } from 'src/luis/projects/analytics/components/TrainingEvaluation/NonTrainedPlaceholder';
import { useCardsTracker } from 'src/luis/projects/analytics/hooks/useCardsTracker';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { analyticsMessages } from 'src/luis/projects/analytics/utils/analyticsMessages';
import { LuisId } from 'src/luis/types/Resource';

export type TrainingEvaluationProps = {
    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId, filters: PassedFilters) => void;
};

const messages = defineMessages({
    title: {
        id: 'analytics.trainingEvaluation.title',
        defaultMessage: 'Training evaluation'
    },
    titleDescription: {
        id: 'analytics.trainingEvaluation.titleDescription',
        defaultMessage: `A summary of the prediction quality from your last training.
            Focus on the unclear and incorrectly predicted intents to improve app predictions.`
    },
    overallCardTitle: {
        id: 'analytics.trainingEvaluation.overallCardTitle',
        defaultMessage: 'Overall predictions'
    },
    overallCardTitleDescription: {
        id: 'analytics.trainingEvaluation.overallCardTitleDescription',
        defaultMessage:
            'The percentage correct is measured by the percentage of utterances for which the predicted intent matches the labeled intent.'
    },
    predictionsPerIntentCardTitle: {
        id: 'analytics.trainingEvaluation.predictionsPerIntentCardTitle',
        defaultMessage: 'Predictions per intent'
    },
    predictionsPerIntentCardTitleDescription: {
        id: 'analytics.trainingEvaluation.predictionsPerIntentCardTitleDescription',
        defaultMessage: `A visual summary of the overall quality of intents. Effective apps have a high-degree of correctly predicted
            example utterances, both within the intent and across intents.`
    },
    intentsWithErrorsCardTitle: {
        id: 'analytics.trainingEvaluation.intentsWithErrorsCardTitle',
        defaultMessage: 'Intents with prediction errors'
    },
    intentsWithErrorsCardTitleDescription: {
        id: 'analytics.trainingEvaluation.intentsWithErrorsCardTitleDescription',
        defaultMessage: 'Select a filter and an intent to see issues with suggestions to improve the intent.'
    },
    subTitleVersion: {
        id: 'analytics.trainingEvaluation.subTitleVersion',
        defaultMessage: 'Active version:'
    },
    subTitleTrained: {
        id: 'analytics.trainingEvaluation.subTitleTrained',
        defaultMessage: 'trained'
    },
    overallCardName: {
        id: 'analytics.trainingEvaluation.overallCardName',
        defaultMessage: 'Overall predictions card.'
    },
    predictionsPerIntentCardName: {
        id: 'analytics.trainingEvaluation.predictionsPerIntentCardName',
        defaultMessage: 'Predictions per intent card.'
    },
    intentsWithErrorsCardName: {
        id: 'analytics.trainingEvaluation.intentsWithErrorsCardName',
        defaultMessage: 'Intents with errors card.'
    }
});

const { styleComponent } = getStylist('Dashboard');

const StyledContainer = styleComponent<ContainerProps>(Container)('StyledContainer', theme =>
    important({ height: 'auto', backgroundColor: theme.backgroundColor })
);

/**
 * The training evaluation section with the three extendable cards:
 * 1. Overall predictions.
 * 2. Predictions per intent.
 * 3. Intents with prediction errors.
 */
export const TrainingEvaluation = observer((props: TrainingEvaluationProps) => {
    const { onIntentClick = noop } = props;
    const { toggleCard, dashboardCardStates, setLastActiveCard } = useCardsTracker();
    const { localize } = useLocalization();
    const { dispatcher, rootStore, uiStore } = useGlobalState();
    const currentAppStore = rootStore.currentLuisAppStore;
    const { analyticsStore } = currentAppStore.currentAppVersionStore;

    React.useEffect(() => {
        if (analyticsStore.serviceStatus === 'notStarted' || analyticsStore.serviceStatus === 'outdated') {
            dispatcher.dispatch('loadMetadata', {
                id: currentAppStore.activeVersion,
                toastData: {
                    running: { title: localize(analyticsMessages.loading) },
                    failed: { title: localize(analyticsMessages.failed) },
                    completed: { title: localize(analyticsMessages.complete) }
                }
            });
        }
    }, [analyticsStore.serviceStatus]);

    /**
     * Generates the the subtitle that contain the currently active version
     * and the last training date for it if it is ever trained.
     */
    const getSubtitle = (): string => {
        let subtitle = `${localize(messages.subTitleVersion)} ${currentAppStore.activeVersion}`;

        if (analyticsStore.lastTrainingDate) {
            subtitle = `${subtitle} – ${localize(messages.subTitleTrained)} ${format(
                analyticsStore.lastTrainingDate,
                LuisDateFormats.Date,
                {
                    locale: getDateLocale(uiStore.locale)
                }
            )}
            ${format(analyticsStore.lastTrainingDate, LuisDateFormats.Time, { locale: getDateLocale(uiStore.locale) })}`;
        }

        return subtitle;
    };

    return (
        <StyledContainer verticalFill size="none">
            <Container size="main" overflow={false}>
                <EditableTitle
                    title={localize(messages.title)}
                    subtitle={getSubtitle()}
                    description={localize(messages.titleDescription)}
                />

                <Stack tokens={{ childrenGap: 34 }}>
                    <ExpandableCard
                        id="overallCard"
                        title={localize(messages.overallCardTitle)}
                        cardName={localize(messages.overallCardName)}
                        onExpansionChange={() => toggleCard('overallPredictions')}
                        onCardClick={() => setLastActiveCard('overallPredictions')}
                        titleDescription={localize(messages.overallCardTitleDescription)}
                        isExpanded={dashboardCardStates.cardsExpandingState.overallPredictions}>
                        {analyticsStore.lastTrainingDate ? (
                            <OverallPredictionsCard onIntentClick={onIntentClick} />
                        ) : (
                            <NonTrainedPlaceholder />
                        )}
                    </ExpandableCard>

                    <ExpandableCard
                        id="predictionsPerIntentCard"
                        title={localize(messages.predictionsPerIntentCardTitle)}
                        cardName={localize(messages.predictionsPerIntentCardName)}
                        onExpansionChange={() => toggleCard('predictionsPerIntent')}
                        onCardClick={() => setLastActiveCard('predictionsPerIntent')}
                        isExpanded={dashboardCardStates.cardsExpandingState.predictionsPerIntent}
                        titleDescription={localize(messages.predictionsPerIntentCardTitleDescription)}>
                        <PredictionsPerIntentCard />
                    </ExpandableCard>

                    <ExpandableCard
                        id="intentsWithErrorsCard"
                        title={localize(messages.intentsWithErrorsCardTitle)}
                        cardName={localize(messages.intentsWithErrorsCardName)}
                        onExpansionChange={() => toggleCard('intentsWithErrors')}
                        onCardClick={() => setLastActiveCard('intentsWithErrors')}
                        isExpanded={dashboardCardStates.cardsExpandingState.intentsWithErrors}
                        titleDescription={localize(messages.intentsWithErrorsCardTitleDescription)}>
                        {analyticsStore.lastTrainingDate ? (
                            <IntentsWithErrorsCard onIntentClick={onIntentClick} />
                        ) : (
                            <NonTrainedPlaceholder />
                        )}
                    </ExpandableCard>
                </Stack>
            </Container>
        </StyledContainer>
    );
});
